import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LibraryRouter } from '@app/models/library.router';
import { ITitleComponentParams } from '@core/components/title/title.component';
import { ChangableComponent } from '@models/changable.component';
import { Content, ContentTypeEnum, EContentPaneFormat, EContentPanelRoute, IContentPane } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { takeUntil } from 'rxjs/operators';
import { AnalyticsService, ScreenService } from '../../core/services';
import { ILibraryTitle } from '../library-title/library-title.component';

@Component({
  selector: 'app-content-drag-scroll',
  templateUrl: './content-drag-scroll.component.html',
  styleUrls: ['./content-drag-scroll.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentDragScrollComponent extends ChangableComponent implements OnInit, OnDestroy {
  private location = inject(Location);
  private screenService = inject(ScreenService);
  deviceService = inject(DeviceDetectorService);
  private analytics = inject(AnalyticsService);

  @ViewChild('scroller', { read: CdkVirtualScrollViewport })
  scroller: CdkVirtualScrollViewport;

  @Input()
  items: Content[] = [];

  @Input()
  title?: ITitleComponentParams = {
    title: 'Уроки',
    route: EContentPanelRoute.lessons,
    more: 'title.show-more',
  };

  @Input()
  libraryTitle: Partial<ILibraryTitle>;

  @Input()
  public set pane(pane: IContentPane) {
    this._pane = pane;
    this.cardSizeTable = this.setCardSizeTable();
  }
  public get pane(): IContentPane {
    return this._pane;
  }
  @Input()
  isLibrary: boolean; // включает новый блок title в новой библиотеке

  public openedCardIndex: number = null;
  public item: Content = null;
  public contentRoute = LibraryRouter.contentRouteUniversal;
  public reachedPrev = false;
  public reachedNext = false;
  public isDragScrollEnabled: boolean;
  public showModal = false;
  public blockWidth = 0;
  public isSmallScreen = true;
  public cardSizeTable: any = { width: 207, height: 415, scrollItem: 1 };
  public chunkNumber: number;

  private originalUrl: string;
  private width = 0;
  public _pane: IContentPane = {
    format: EContentPaneFormat.meetings['card-mini'],
    order: 0,
    loaded: false,
  };

  public ngOnInit(): void {
    this.isDragScrollEnabled = this.deviceService.isDesktop();
    this.screenService.width$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (width) => {
        this.width = width;
        this.blockWidth = width > 920 ? 830 : width > 640 ? width - 40 : width - 30;
        this.isSmallScreen = this.screenService.isSmallScreen;
      },
      error: (error) => {
        this.width = window.innerWidth;
        this.blockWidth = this.width > 920 ? 830 : this.width > 640 ? this.width - 40 : this.width - 30;
        this.isSmallScreen = this.screenService.isSmallScreen;
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });

    if (this.libraryTitle.see_all_link === 'no-link') {
      this.libraryTitle.see_all_link = '';
    }
  }

  public get urlRoute() {
    return typeof this.title.route !== 'string'
      ? this.title.route.map((item, index) => (index === 1 ? (item = 'create') : item)).join('/')
      : null;
  }

  moveLeft() {
    this.scroller.scrollToIndex(0, 'smooth');
  }

  moveRight() {
    const offset = this.scroller.measureScrollOffset('left');
    // сдвинуть например на 3 карточки
    this.scroller.scrollToOffset(offset + this.cardSizeTable.scrollItem * this.cardSizeTable.width, 'smooth');
  }

  public onOpenCard(item: Content) {
    // logger.log('ContentDragScrollComponent:onOpenCard', item);
    this.openedCardIndex = this.items.findIndex((el) => el.id === item.id);
    this.item = item;
    const url = this.contentRoute(this.item);
    const currentUrl = this.location.path(false);
    if (currentUrl !== url) {
      if (!this.originalUrl) {
        this.originalUrl = currentUrl;
      }
      this.location.replaceState(url);

      // отправка событий в аналитику для имитации навигации по страницам
      this.analytics.pageView(url, currentUrl);
    }
    this.checkReached();
    this.showModal = this.item && this.isLesson;
  }

  public onCloseCard() {
    // logger.debug('ContentDragScrollComponent:onCloseCard');
    this.openedCardIndex = null;
    this.item = null;
    if (this.originalUrl) {
      this.location.replaceState(this.originalUrl);
      this.originalUrl = null;
    }
    // this.showPopover = false;
    this.showModal = false;
  }

  public onCloseModal(event) {
    if (event) {
      this.onCloseCard();
    }
  }

  public checkReached() {
    const reachedPrev = this.openedCardIndex <= 0;
    const reachedNext = this.openedCardIndex >= this.items.length - 1;

    if (!reachedPrev && !reachedNext) {
      this.reachedPrev = this.reachedNext = false;
    } else if (reachedPrev && reachedNext) {
      this.reachedPrev = this.reachedNext = true;
    } else if (reachedPrev) {
      this.reachedPrev = true;
    } else if (reachedNext) {
      this.reachedNext = true;
    }
  }

  public togglePrev(event?) {
    if (this.openedCardIndex - 1 >= 0) {
      this.onOpenCard(this.items[this.openedCardIndex - 1]);
    }
    this.checkReached();
    this.detectChanges();
  }

  public toggleNext(event?) {
    if (this.openedCardIndex + 1 <= this.items.length - 1) {
      this.onOpenCard(this.items[this.openedCardIndex + 1]);
    }
    this.checkReached();
    this.detectChanges();
  }

  public get isLesson(): boolean {
    // return this.item ? LessonContentType.includes(this.item.type) : false;
    return true; // Это нужно чтобы открывать все в модальном окне, не только уроки
  }

  // функция для трекинга списка, чтобы он не 'моргал'
  public trackContentByFn(index, item: Content): string {
    return item.id;
  }

  public trackContentChunkByFn(index): number {
    return index;
  }

  public setCardSizeTable(): { width: number; height: number; scrollItem: number } {
    const defaultSize = { width: 207, height: 385, scrollItem: 1 };

    const paneFormat: string = this.pane.format;
    const contentFilter = this.pane.contentFilter.content_type
      ? this.pane.contentFilter.content_type[0] || this.pane.contentFilter.content_type
      : null;

    let size = {};

    switch (Number(contentFilter)) {
      case Number(ContentTypeEnum.video): // 'library.what-to-see':
        size = {
          ['card-universal']: { width: 207, height: 416, scrollItem: 3 },
          ['card-micro']: { width: 207, height: 416, scrollItem: 3 },
          ['card-mini']: { width: 207, height: 416, scrollItem: 3 },
          ['card-mid']: { width: 276, height: 310, scrollItem: 1 },
        };
        return size[paneFormat];
      case Number(ContentTypeEnum.audio): // 'library.what-to-listen':
        size = {
          ['card-universal']: { width: 207, height: 416, scrollItem: 3 },
          ['card-micro']: { width: 415, height: 100, scrollItem: 1 },
          ['card-mini']: { width: 207, height: 416, scrollItem: 3 },
          ['card-mid']: { width: 276, height: 416, scrollItem: 1 },
        };
        return size[paneFormat];
      case Number(ContentTypeEnum.article): // 'library.what-to-read':
      case Number(ContentTypeEnum.news):
        size = {
          ['card-universal']: { width: 207, height: 416, scrollItem: 3 },
          ['card-micro']: { width: 415, height: 464, scrollItem: 1 },
          ['card-mini']: { width: 207, height: 416, scrollItem: 3 },
          ['card-mid']: { width: 276, height: 416, scrollItem: 1 },
        };
        return size[paneFormat];
      default:
        return defaultSize;
    }
  }

  public get rows() {
    return this.pane.rows ? this.pane.rows : 1;
  }

  public get cols() {
    return this.pane.cols ? this.pane.cols : 4;
  }

  public chunk(arr, size = 1) {
    return arr.reduce((acc, _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]), []);
  }

  public height(): number {
    const isFullContent = this.items.length >= this.rows * this.cols;
    const marginCol = 10; // отступ между картачками в колонке
    const paddingCol = 30; // суммарный отступ сверху + снизу в колонке

    if (isFullContent) {
      this.chunkNumber = this.rows;
      return (this.cardSizeTable.height + marginCol) * this.rows - marginCol + paddingCol;
    } else if (this.items.length === 1) {
      this.chunkNumber = 1;
      return this.cardSizeTable.height + paddingCol;
    } else {
      this.chunkNumber = Math.ceil(this.items.length / this.cols);
      return this.chunkNumber === 1
        ? this.chunkNumber * this.cardSizeTable.height + paddingCol
        : this.chunkNumber * (this.cardSizeTable.height + marginCol) + paddingCol;
    }
  }

  public get paneFormat() {
    if (!this.pane) {
      return;
    }
    if (this.deviceService.isMobile() && this.pane.replace_for_mobile) {
      if (this.pane.format === EContentPaneFormat['new-grid-universal-card']) {
        return this.pane.replace_for_mobile_pane;
      }
    }

    return this.pane.format;
  }
}
